import React, { useState } from 'react';
import { SearchIcon, DownloadIcon, EyeIcon } from 'lucide-react';
export function UserActivityAuditLog() {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [username, setUsername] = useState('');
  const [activity, setActivity] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedLog, setSelectedLog] = useState<any>(null);
  // Sample activities
  const activities = [{
    id: '',
    name: 'All Activities'
  }, {
    id: 'login',
    name: 'Login'
  }, {
    id: 'logout',
    name: 'Logout'
  }, {
    id: 'create_user',
    name: 'Create User'
  }, {
    id: 'modify_user',
    name: 'Modify User'
  }, {
    id: 'delete_user',
    name: 'Delete User'
  }, {
    id: 'create_role',
    name: 'Create Role'
  }, {
    id: 'modify_role',
    name: 'Modify Role'
  }, {
    id: 'delete_role',
    name: 'Delete Role'
  }, {
    id: 'update_config',
    name: 'Update Configuration'
  }];
  // Sample users
  const users = [{
    id: '',
    name: 'All Users'
  }, {
    id: 'john.doe',
    name: 'John Doe'
  }, {
    id: 'jane.smith',
    name: 'Jane Smith'
  }, {
    id: 'admin',
    name: 'Admin User'
  }, {
    id: 'operator1',
    name: 'Operator 1'
  }, {
    id: 'manager1',
    name: 'Manager 1'
  }];
  // Sample audit log data
  const sampleAuditData = [{
    id: 1,
    timestamp: '2023-05-10 14:30:22',
    username: 'john.doe',
    userFullName: 'John Doe',
    activity: 'login',
    activityName: 'Login',
    ipAddress: '192.168.1.100',
    status: 'success',
    details: {
      browser: 'Chrome 113.0.0',
      os: 'Windows 10',
      location: 'Addis Ababa, Ethiopia'
    }
  }, {
    id: 2,
    timestamp: '2023-05-10 14:45:15',
    username: 'john.doe',
    userFullName: 'John Doe',
    activity: 'create_user',
    activityName: 'Create User',
    ipAddress: '192.168.1.100',
    status: 'success',
    details: {
      createdUser: 'new.user@example.com',
      role: 'Operator',
      actionBy: 'john.doe'
    }
  }, {
    id: 3,
    timestamp: '2023-05-10 15:10:47',
    username: 'jane.smith',
    userFullName: 'Jane Smith',
    activity: 'modify_role',
    activityName: 'Modify Role',
    ipAddress: '192.168.1.101',
    status: 'success',
    details: {
      roleName: 'Operator',
      changes: 'Added new permissions',
      actionBy: 'jane.smith'
    }
  }, {
    id: 4,
    timestamp: '2023-05-10 15:30:33',
    username: 'admin',
    userFullName: 'Admin User',
    activity: 'update_config',
    activityName: 'Update Configuration',
    ipAddress: '192.168.1.102',
    status: 'success',
    details: {
      parameter: 'Password Policy',
      oldValue: '30 days',
      newValue: '90 days',
      actionBy: 'admin'
    }
  }, {
    id: 5,
    timestamp: '2023-05-10 16:15:19',
    username: 'john.doe',
    userFullName: 'John Doe',
    activity: 'logout',
    activityName: 'Logout',
    ipAddress: '192.168.1.100',
    status: 'success',
    details: {
      browser: 'Chrome 113.0.0',
      os: 'Windows 10',
      location: 'Addis Ababa, Ethiopia'
    }
  }, {
    id: 6,
    timestamp: '2023-05-09 09:30:22',
    username: 'jane.smith',
    userFullName: 'Jane Smith',
    activity: 'login',
    activityName: 'Login',
    ipAddress: '192.168.1.101',
    status: 'failed',
    details: {
      browser: 'Firefox 112.0.0',
      os: 'Ubuntu 20.04',
      location: 'Addis Ababa, Ethiopia',
      reason: 'Invalid password'
    }
  }, {
    id: 7,
    timestamp: '2023-05-09 09:31:15',
    username: 'jane.smith',
    userFullName: 'Jane Smith',
    activity: 'login',
    activityName: 'Login',
    ipAddress: '192.168.1.101',
    status: 'success',
    details: {
      browser: 'Firefox 112.0.0',
      os: 'Ubuntu 20.04',
      location: 'Addis Ababa, Ethiopia'
    }
  }, {
    id: 8,
    timestamp: '2023-05-09 10:45:33',
    username: 'jane.smith',
    userFullName: 'Jane Smith',
    activity: 'delete_user',
    activityName: 'Delete User',
    ipAddress: '192.168.1.101',
    status: 'success',
    details: {
      deletedUser: 'old.user@example.com',
      actionBy: 'jane.smith',
      reason: 'No longer with company'
    }
  }];
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Validate date range
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      const diffTime = Math.abs(to.getTime() - from.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 90) {
        alert('Date range cannot exceed 90 days');
        setIsLoading(false);
        return;
      }
      if (from > to) {
        alert('From date must be before To date');
        setIsLoading(false);
        return;
      }
    }
    // Simulate API delay
    setTimeout(() => {
      // Filter sample data based on search criteria
      let results = [...sampleAuditData];
      // Filter by username if selected
      if (username) {
        results = results.filter(log => log.username === username);
      }
      // Filter by activity if selected
      if (activity) {
        results = results.filter(log => log.activity === activity);
      }
      // Sort by timestamp (newest first)
      results.sort((a, b) => {
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      });
      setSearchResults(results);
      setHasSearched(true);
      setIsLoading(false);
    }, 800);
  };
  const handleReset = () => {
    setFromDate('');
    setToDate('');
    setUsername('');
    setActivity('');
    setSearchResults([]);
    setHasSearched(false);
  };
  const handleExport = (format: 'csv' | 'pdf') => {
    // In a real app, you would implement the actual export functionality
    alert(`Exporting as ${format.toUpperCase()}...`);
  };
  const handleViewDetails = (log: any) => {
    setSelectedLog(log);
    setShowDetails(true);
  };
  return <div className="space-y-6" data-id="element-1066">
      <h1 className="text-2xl font-bold text-[#1A1F36]" data-id="element-1067">
        User Activity Audit Log
      </h1>
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6" data-id="element-1068">
        <form onSubmit={handleSearch} className="space-y-6" data-id="element-1069">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1070">
            <div className="space-y-2" data-id="element-1071">
              <label htmlFor="fromDate" className="block text-sm font-medium text-gray-700" data-id="element-1072">
                From Date
              </label>
              <input id="fromDate" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" required data-id="element-1073" />
            </div>
            <div className="space-y-2" data-id="element-1074">
              <label htmlFor="toDate" className="block text-sm font-medium text-gray-700" data-id="element-1075">
                To Date
              </label>
              <input id="toDate" type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" required data-id="element-1076" />
            </div>
            <div className="space-y-2" data-id="element-1077">
              <label htmlFor="username" className="block text-sm font-medium text-gray-700" data-id="element-1078">
                User
              </label>
              <select id="username" value={username} onChange={e => setUsername(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-1079">
                {users.map(user => <option key={user.id} value={user.id} data-id="element-1080">
                    {user.name}
                  </option>)}
              </select>
            </div>
            <div className="space-y-2" data-id="element-1081">
              <label htmlFor="activity" className="block text-sm font-medium text-gray-700" data-id="element-1082">
                Activity
              </label>
              <select id="activity" value={activity} onChange={e => setActivity(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-1083">
                {activities.map(act => <option key={act.id} value={act.id} data-id="element-1084">
                    {act.name}
                  </option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-1085">
            <button type="submit" disabled={isLoading} className="px-4 py-2 bg-[#F58220] text-white rounded-md font-medium hover:bg-[#e07619] focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:ring-opacity-50 flex items-center" data-id="element-1086">
              {isLoading ? <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1087">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1088"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1089"></path>
                  </svg>
                  Generating...
                </> : <>
                  <SearchIcon size={16} className="inline mr-2" data-id="element-1090" />
                  Generate Report
                </>}
            </button>
            <button type="button" onClick={handleReset} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50" data-id="element-1091">
              Reset
            </button>
          </div>
        </form>
      </div>
      {hasSearched && <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-1092">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1093">
            <h3 className="font-medium text-[#1A1F36]" data-id="element-1094">Audit Log Results</h3>
            {searchResults.length > 0 && <div className="flex items-center gap-3" data-id="element-1095">
                <button onClick={() => handleExport('csv')} className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1096">
                  <DownloadIcon size={16} className="mr-2" data-id="element-1097" />
                  Export CSV
                </button>
                <button onClick={() => handleExport('pdf')} className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1098">
                  <DownloadIcon size={16} className="mr-2" data-id="element-1099" />
                  Export PDF
                </button>
              </div>}
          </div>
          {searchResults.length === 0 ? <div className="p-8 text-center" data-id="element-1100">
              <p className="text-gray-500" data-id="element-1101">
                No audit logs found matching your search criteria.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-1102">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1103">
                <thead className="bg-gray-50" data-id="element-1104">
                  <tr data-id="element-1105">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1106">
                      Date & Time
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1107">
                      User
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1108">
                      Activity
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1109">
                      IP Address
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1110">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1111">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1112">
                  {searchResults.map(log => <tr key={log.id} className="hover:bg-gray-50" data-id="element-1113">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1114">
                        {log.timestamp}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1115">
                        {log.userFullName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1116">
                        {log.activityName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1117">
                        {log.ipAddress}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1118">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${log.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1119">
                          {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1120">
                        <button onClick={() => handleViewDetails(log)} className="text-blue-600 hover:text-blue-800 flex items-center" data-id="element-1121">
                          <EyeIcon size={16} className="mr-1" data-id="element-1122" />
                          View
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>}
      {/* Details Modal */}
      {showDetails && selectedLog && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1123">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6" data-id="element-1124">
            <div className="flex justify-between items-center mb-4" data-id="element-1125">
              <h3 className="text-lg font-semibold text-[#1A1F36]" data-id="element-1126">
                Activity Details
              </h3>
              <button onClick={() => setShowDetails(false)} className="text-gray-500 hover:text-gray-700" data-id="element-1127">
                &times;
              </button>
            </div>
            <div className="space-y-4" data-id="element-1128">
              <div className="grid grid-cols-2 gap-4" data-id="element-1129">
                <div data-id="element-1130">
                  <p className="text-sm text-gray-500" data-id="element-1131">Date & Time</p>
                  <p className="text-sm font-medium" data-id="element-1132">{selectedLog.timestamp}</p>
                </div>
                <div data-id="element-1133">
                  <p className="text-sm text-gray-500" data-id="element-1134">User</p>
                  <p className="text-sm font-medium" data-id="element-1135">
                    {selectedLog.userFullName}
                  </p>
                </div>
                <div data-id="element-1136">
                  <p className="text-sm text-gray-500" data-id="element-1137">Activity</p>
                  <p className="text-sm font-medium" data-id="element-1138">
                    {selectedLog.activityName}
                  </p>
                </div>
                <div data-id="element-1139">
                  <p className="text-sm text-gray-500" data-id="element-1140">IP Address</p>
                  <p className="text-sm font-medium" data-id="element-1141">{selectedLog.ipAddress}</p>
                </div>
                <div data-id="element-1142">
                  <p className="text-sm text-gray-500" data-id="element-1143">Status</p>
                  <p className={`text-sm font-medium ${selectedLog.status === 'success' ? 'text-green-600' : 'text-red-600'}`} data-id="element-1144">
                    {selectedLog.status.charAt(0).toUpperCase() + selectedLog.status.slice(1)}
                  </p>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-4" data-id="element-1145">
                <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-1146">
                  Additional Details
                </p>
                <div className="bg-gray-50 p-3 rounded-md" data-id="element-1147">
                  <pre className="text-xs overflow-auto" data-id="element-1148">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end" data-id="element-1149">
              <button onClick={() => setShowDetails(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300" data-id="element-1150">
                Close
              </button>
            </div>
          </div>
        </div>}
    </div>;
}